import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ChooseContract } from './ChooseContract';
import { OrdersHeader } from './OrdersHeader';
import { OrdersTable } from './OrdersTable';

import { ApplicationState } from '../../../store';

import {
  loadNewLongSteelOrders,
  loadNewLongSteelOrdersFromLocalStorage
} from '../../../store/ducks/new-long-steel-orders/actions';
import { EOrderTab } from '../../../store/ducks/long-steel-orders/types';
import {
  ELongSteelOrdersStatus,
  INewLongSteelOrderData,
  SalesOrderItemTable
} from '../../../store/ducks/new-long-steel-orders/types';
import {
  checkCrossSellAvailable,
  cleanCrossSellItems
} from '../../../store/ducks/cross-sell/actions';
import { GetFlag } from '../../../utils/featureFlag';
import {
  FEATURE_FLAG_CROSS_SELL,
  FEATURE_FLAG_CROSS_SELL_DYNAMIC,
  FEATURE_FLAG_CROSS_SELL_ORDER_DETAIL,
  FEATURE_FLAG_CROSS_SELL_VERTICAL
} from '../../../utils/cross-sell';
import { useApiInstance } from '../../../services/MVP/useApiInstance';
import { CrossSellProducts } from '../../Purchase/LongSteel/ShoppingCartMVP/CrossSellProducts';
import useIsCommerceUser from '../../../hooks/useIsCommerceUser';

import {
  Container,
  PageWrapper,
  ContentArea,
  CrossSellSidebar
} from './styles';

export const NewLongSteelOrders: React.FC = () => {
  const dispatch = useDispatch();

  const ordersState = useSelector(
    (state: ApplicationState) => state.newLongSteelOrders
  );
  const isManager = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const crossSellState = useSelector(
    (state: ApplicationState) => state.crossSell
  );
  const crossSellItems = useSelector((state: ApplicationState) =>
    state.crossSell.items.filter(
      (p) => !!p.price?.creditPrice && Number(p.price.creditPrice) > 0
    )
  );
  const crossSellSelectedBp = useSelector(
    (state: ApplicationState) => state.crossSell.selectedBusinessPartner
  );

  const activeTabState = ordersState[ordersState.tableType || EOrderTab.OPEN];

  const [filteredOrders, setFilteredOrders] = useState<
    INewLongSteelOrderData[]
  >(activeTabState.items);
  const [selectedBPs, setSelectedBPs] = useState<string[]>(
    activeTabState.initialInputValues?.emitterIds ?? []
  );
  const [, setCrossSellKey] = useState('');
  const [crossSellDismissed, setCrossSellDismissed] = useState(false);
  const lastCrossSellBPsRef = useRef('');

  const crossSellFeatureFlag = GetFlag(FEATURE_FLAG_CROSS_SELL);
  const crossSellOrderDetailFlag = GetFlag(
    FEATURE_FLAG_CROSS_SELL_ORDER_DETAIL
  );
  const crossSellNewFlow = GetFlag(FEATURE_FLAG_CROSS_SELL_DYNAMIC);
  const crossSellVerticalFlag = GetFlag(FEATURE_FLAG_CROSS_SELL_VERTICAL);

  const isPurchaseMvp = useIsCommerceUser();
  const commerceApi = useApiInstance();

  const initOrders = () => {
    dispatch(loadNewLongSteelOrdersFromLocalStorage());
  };

  const reloadOrders = () => {
    const { states, emitterIds, startDate, endDate } =
      activeTabState.initialInputValues;
    loadOrders({ states, customerIds: emitterIds, startDate, endDate });
  };

  useEffect(() => {
    if (
      !customersState.loading &&
      !customersState.error &&
      customersState.customers.length > 0
    ) {
      if (!activeTabState.initialized && !isManager) {
        initOrders();
      } else if (activeTabState.lastLoadTime !== undefined) {
        if (new Date().getTime() - activeTabState.lastLoadTime > 3600000) {
          // recarrega as ordens se tiver passado UMA HORA desde o último carregamento
          reloadOrders();
        }
      }
    }
  });

  useEffect(() => {
    if (activeTabState.items) {
      setFilteredOrders(activeTabState.items as SalesOrderItemTable[]);
    }
  }, [activeTabState.items]);

  useEffect(() => {
    if (
      crossSellFeatureFlag &&
      crossSellOrderDetailFlag &&
      isPurchaseMvp &&
      selectedBPs.length > 0 &&
      activeTabState.initialized
    ) {
      setCrossSellDismissed(false);
      const bpsKey = [...selectedBPs].sort().join(',');
      if (bpsKey === lastCrossSellBPsRef.current) return;
      lastCrossSellBPsRef.current = bpsKey;
      dispatch(cleanCrossSellItems());
      dispatch(
        checkCrossSellAvailable({
          businessPartners: selectedBPs,
          commerceApi,
          useNewFlow: crossSellNewFlow,
          scope: 'orders'
        })
      );
    }
  }, [selectedBPs, activeTabState.initialized]);

  useEffect(() => {
    dispatch(cleanCrossSellItems());
    return () => {
      dispatch(cleanCrossSellItems());
    };
  }, []);

  const loadOrders = ({
    states,
    customerIds,
    startDate,
    endDate
  }: {
    states: string[];
    customerIds: string[];
    startDate?: string;
    endDate?: string;
  }) => {
    const needToLoadCustomer =
      isManager &&
      !customersState.customers?.find((c) => c.customerId === customerIds[0]);

    dispatch(
      loadNewLongSteelOrders({
        states,
        customerIds,
        customerViewType,
        startDate,
        endDate,
        needToLoadCustomer,
        isManager,
        orderType: ordersState.tableType
      })
    );
  };

  const onCleanFilters = () => {
    activeTabState.initialInputValues.orderStatus = ELongSteelOrdersStatus.ALL;
    setFilteredOrders(activeTabState.items as SalesOrderItemTable[]);
  };

  const isFiltered = () => {
    return filteredOrders.length !== activeTabState.items.length;
  };

  const loadingState = customersState.loading || ordersState.loading;

  const inputsManager =
    activeTabState?.initialInputValues?.emitterIds.length > 0 &&
    !!activeTabState?.initialInputValues?.startDate &&
    !!activeTabState?.initialInputValues?.endDate;

  const inputsFill =
    (isManager && inputsManager) ||
    Object.entries(activeTabState?.initialInputValues ?? {}).filter(
      ([key, value]) =>
        key !== 'receiverId' && (value as string | string[])?.length === 0
    ).length === 0;

  const hasInitialized = inputsFill && activeTabState.initialized;

  const showCrossSell =
    isPurchaseMvp && crossSellFeatureFlag && crossSellOrderDetailFlag;
  const hasCrossSellContent =
    crossSellState.loading || crossSellItems.length > 0;
  const showVertical =
    showCrossSell && crossSellVerticalFlag && hasCrossSellContent;

  const handleEmittersChange = (bps: string[]) => {
    setSelectedBPs(bps);
    setCrossSellDismissed(false);
  };

  const firstOrderForCrossSell =
    selectedBPs.length > 0
      ? filteredOrders.find((order) =>
          selectedBPs.includes(order.soldTo?.code ?? '')
        ) ?? filteredOrders[0]
      : filteredOrders[0];
  const orderForSelectedBp =
    selectedBPs.length > 1 && crossSellSelectedBp
      ? filteredOrders.find(
          (order) => order.soldTo?.code === crossSellSelectedBp
        ) ?? firstOrderForCrossSell
      : firstOrderForCrossSell;
  const crossSellOrderSoldTo = orderForSelectedBp?.soldTo?.code;
  const crossSellOrderShipTo = orderForSelectedBp?.shipTo?.code;

  return (
    <Container>
      <PageWrapper showVerticalCrossSell={showVertical && !crossSellDismissed}>
        <ContentArea scrollable={showVertical && !crossSellDismissed}>
          <OrdersHeader
            {...{
              isFiltered: isFiltered(),
              onCleanFilters,
              onLoadOrders: loadOrders,
              setFilteredOrders,
              onEmittersChange: handleEmittersChange
            }}
          />

          <OrdersTable hasInitialized={hasInitialized} />

          <ChooseContract
            hasContract={!hasInitialized}
            isLoading={loadingState}
          />
        </ContentArea>

        {showVertical && !crossSellDismissed && (
          <CrossSellSidebar>
            <CrossSellProducts
              setCrossSellKey={setCrossSellKey}
              orientation="vertical"
              filteredItems={crossSellItems}
              screenName="LG:MeusPedidos"
              orderSoldTo={crossSellOrderSoldTo}
              orderShipTo={crossSellOrderShipTo}
              showBpInfo={selectedBPs.length > 0}
              crossSellContext="orders"
              onDismiss={() => setCrossSellDismissed(true)}
            />
          </CrossSellSidebar>
        )}
      </PageWrapper>
    </Container>
  );
};
